// Стрелки связей поверх тела сетки: от конца блокера к началу заблокированной. Чистая геометрия отдельно,
// чтобы её можно было проверить без DOM; при наведении на задачу её связи выделяются.
import { addDays } from '@core/dates';
import { barGeometry, trackTop, type Grid } from '../roadmap/shared/geometry';
import type { LaunchRow, LaunchTask, Link, Preview } from './planner';

export type Segment = { id: string; blocker: string; blocked: string; x1: number; y1: number; x2: number; y2: number };

export type Placed = { rowKey: string; sub: number; start_date: string; end_date: string };

/** Верх каждой строки тела по высотам подряд и высота тела. */
export function rowTops(rows: { key: string; height: number }[]): { tops: Map<string, number>; height: number } {
  const tops = new Map<string, number>();
  let y = 0;
  for (const r of rows) { tops.set(r.key, y); y += r.height; }
  return { tops, height: y };
}

/** Где задача показана, пока её тащат: контур броска вместо старого места, чтобы стрелки не рвались; группа — все на shiftDays. */
export function overrideOf(drag: { task: LaunchTask; group: string[]; preview: Preview | null } | null, shiftDays: number | null, rows: LaunchRow[], fresh: (id: string) => LaunchTask | undefined): Map<string, Placed> {
  const out = new Map<string, Placed>();
  if (!drag?.preview) return out;
  const p = drag.preview;
  const t = fresh(drag.task.id) ?? drag.task;
  const trackRow = (trackId: string) => rows.find((r): r is Extract<LaunchRow, { kind: 'track' }> => r.kind === 'track' && r.id === trackId);
  if (p.kind === 'resize') out.set(t.id, { rowKey: `t:${t.track_id}`, sub: trackRow(t.track_id)?.subRow.get(t.id) ?? 0, start_date: p.start_date, end_date: p.end_date });
  else if (drag.group.length > 1 && shiftDays !== null) {
    for (const id of drag.group) {
      const g = fresh(id);
      if (g) out.set(g.id, { rowKey: `t:${g.track_id}`, sub: trackRow(g.track_id)?.subRow.get(g.id) ?? 0, start_date: addDays(g.start_date, shiftDays), end_date: addDays(g.end_date, shiftDays) });
    }
  } else out.set(t.id, { rowKey: `t:${p.track_id}`, sub: trackRow(p.track_id)?.subRows ?? 0, start_date: p.start_date, end_date: p.end_date }); // контур ложится в новый подряд снизу
  return out;
}

/**
 * rowTop — верх строки трека в теле; barHOf — высота бара строки. Связь вне сетки (обе задачи вне диапазона)
 * пропускается. override — где задача сейчас показана, если её тащат (контур броска), чтобы стрелки не рвались.
 */
export function linkSegments(links: Iterable<Link>, rows: LaunchRow[], tasks: Map<string, LaunchTask>, grid: Grid, rowTop: Map<string, number>, barHOf: (rowKey: string) => number, override: Map<string, Placed> = new Map()): Segment[] {
  const place = new Map<string, Placed>();
  for (const r of rows) if (r.kind === 'track') for (const [id, sub] of r.subRow) { const t = tasks.get(id); if (t) place.set(id, { rowKey: r.key, sub, start_date: t.start_date, end_date: t.end_date }); }
  for (const [id, p] of override) place.set(id, p);
  const out: Segment[] = [];
  for (const l of links) {
    const a = tasks.get(l.blocker_id), b = tasks.get(l.blocked_id);
    const pa = a && place.get(a.id), pb = b && place.get(b.id);
    if (!a || !b || !pa || !pb) continue;
    const ga = barGeometry(pa, grid), gb = barGeometry(pb, grid);
    if (!ga || !gb) continue;
    const ha = barHOf(pa.rowKey), hb = barHOf(pb.rowKey);
    out.push({
      id: l.id, blocker: a.id, blocked: b.id,
      x1: ga.left + ga.width, y1: (rowTop.get(pa.rowKey) ?? 0) + trackTop(pa.sub, ha) + ha / 2,
      x2: gb.left, y2: (rowTop.get(pb.rowKey) ?? 0) + trackTop(pb.sub, hb) + hb / 2,
    });
  }
  return out;
}

/** Ломаная: вправо от конца блокера, вниз/вверх, к началу заблокированной; если она левее — с обходом. */
const LINK_GAP = 10;    // отступ ломаной от концов баров
const LINK_DETOUR = 14; // на сколько уйти вниз/вверх, когда заблокированная левее блокера

export function linkPath(s: Segment): string {
  const gap = LINK_GAP;
  if (s.x2 >= s.x1 + 2 * gap) {
    const mx = s.x1 + gap;
    return `M ${s.x1} ${s.y1} H ${mx} V ${s.y2} H ${s.x2}`;
  }
  const dy = s.y2 > s.y1 ? 1 : -1;
  const my = s.y1 + dy * LINK_DETOUR;
  return `M ${s.x1} ${s.y1} H ${s.x1 + gap} V ${my} H ${s.x2 - gap} V ${s.y2} H ${s.x2}`;
}

export function LinksLayer({ segments, width, height, hovered }: { segments: Segment[]; width: number; height: number; hovered: string | null }) {
  if (segments.length === 0) return null;
  return (
    <svg className="absolute inset-0 pointer-events-none z-[5]" width={width} height={height} data-launch-links={segments.length} aria-hidden>
      <defs>
        <marker id="launch-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="8" markerHeight="8" orient="auto-start-reverse"><path d="M 0 0 L 8 4 L 0 8 z" fill="currentColor" /></marker>
      </defs>
      {segments.map((s) => {
        const hot = hovered !== null && (s.blocker === hovered || s.blocked === hovered);
        return <path key={s.id} data-launch-link={s.id} d={linkPath(s)} fill="none" stroke="currentColor" markerEnd="url(#launch-arrow)"
          className={hot ? 'text-red-600' : hovered ? 'text-gray-300' : 'text-gray-500'} strokeWidth={hot ? 2.5 : 1.5} />;
      })}
    </svg>
  );
}
